import { type FC, useMemo } from "react";

import { Box, Card, Skeleton, Table } from "@mantine/core";

import { dateFormat } from "../../camkit/common/formatters/formatDate";
import { RemainingDaysStatus } from "../../lens-tags/components/TrendingTagsListColumns.styles";
import { useGetTrendingTags } from "../../lens-tags/hooks/useGetTrendingTags";
import { EXPIRING_SOON_THRESHOLD_DAYS, isChallengeTag } from "../../lens-tags/utils";

import { challengeRow, sectionTitle, tableHeader } from "./HomeTrendingSection.module.css";
import { TableHeaderEvent, TableHeaderExpirationDate, TableHeaderTag, TrendingTags } from "./messages.generated";

const SKELETON_ROW_COUNT = 5;
const TEXT_LINE_HEIGHT = 14;
const MS_PER_DAY = 1000 * 3600 * 24;

const isExpiringSoon = (expiresAt: string): boolean => {
    const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / MS_PER_DAY);
    return days <= EXPIRING_SOON_THRESHOLD_DAYS;
};

const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
    <Table.Tr key={index}>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width="70%" />
        </Table.Td>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width="60%" />
        </Table.Td>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width={80} />
        </Table.Td>
    </Table.Tr>
));

export const HomeTrendingTagsTable: FC = () => {
    const { data, loading } = useGetTrendingTags();

    const rows = useMemo(
        () =>
            data.map((tag) => (
                <Table.Tr key={tag.id} className={isChallengeTag(tag.tag) ? challengeRow : undefined}>
                    <Table.Td className="paragraph-p300">
                        {tag.event} {tag.location.graphicUnicode}
                    </Table.Td>
                    <Table.Td className="paragraph-p300">#{tag.tag}</Table.Td>
                    <Table.Td className="paragraph-p300">
                        {dateFormat.format(new Date(tag.expiresAt))}
                        {isExpiringSoon(tag.expiresAt) && (
                            <>
                                {" "}
                                <RemainingDaysStatus status="warning" />
                            </>
                        )}
                    </Table.Td>
                </Table.Tr>
            )),
        [data],
    );

    return (
        <Box>
            <h3 className={`heading-h400 ${sectionTitle}`}>
                <TrendingTags />
            </h3>
            <Card shadow="sm" radius="md" p={5}>
                <Table verticalSpacing="md" withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th className={tableHeader}>
                                <TableHeaderEvent />
                            </Table.Th>
                            <Table.Th className={tableHeader}>
                                <TableHeaderTag />
                            </Table.Th>
                            <Table.Th className={tableHeader} w={140}>
                                <TableHeaderExpirationDate />
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{loading ? skeletonRows : rows}</Table.Tbody>
                </Table>
            </Card>
        </Box>
    );
};
